import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ConfigProvider } from "@/context/ConfigContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DataEntry from "@/pages/DataEntry";
import PIPs from "@/pages/PIPs";
import Insights from "@/pages/Insights";
import Reports from "@/pages/Reports";
import SurveyReadiness from "@/pages/SurveyReadiness";
import PredictiveAnalytics from "@/pages/PredictiveAnalytics";
import CustomMetrics from "@/pages/CustomMetrics";
import Trends from "@/pages/Trends";
import BatchImport from "@/pages/BatchImport";
import Compliance from "@/pages/Compliance";
import Security from "@/pages/Security";
import DashboardSettings from "@/pages/DashboardSettings";
import Collaboration from "@/pages/Collaboration";
import AutoPIP from "@/pages/AutoPIP";
import AutoPIPAnalytics from "@/pages/AutoPIPAnalytics";
import RNCMProductivity from "@/pages/RNCMProductivity";
import PTOCalendar from "@/pages/PTOCalendar";
import HiringPredictions from "@/pages/HiringPredictions";
import ComplianceDashboard from "@/pages/ComplianceDashboard";
import AdmissionsDischarges from "@/pages/AdmissionsDischarges";
import DigestSettings from "@/pages/DigestSettings";
import ExecutePIPs from "@/pages/ExecutePIPs";
import EmailSettings from "@/pages/EmailSettings";
import Leaderboard from "@/pages/Leaderboard";
import Benchmarking from "@/pages/Benchmarking";
import CustomizeDashboard from "@/pages/CustomizeDashboard";
import Training from "@/pages/Training";
import EHRIntegration from "@/pages/EHRIntegration";
import PIPPredictions from "@/pages/PIPPredictions";
import ExportReports from "@/pages/ExportReports";
import UserManagement from "@/pages/UserManagement";
import BurnoutDashboard from "@/pages/BurnoutDashboard";
import AIAgent from "@/pages/AIAgent";
import AIIntelligence from "@/pages/AIIntelligence";
import WorkforceIntelligence from "@/pages/WorkforceIntelligence";
import ClinicalQuality from "@/pages/ClinicalQuality";
import PatientExperience from "@/pages/PatientExperience";
import FinancialHealth from "@/pages/FinancialHealth";
import CHAPReadiness from "@/pages/CHAPReadiness";
import HIPAACompliance from "@/pages/HIPAACompliance";
import MonthlyFinancialDashboard from "@/pages/MonthlyFinancialDashboard";
import KPISyncMonitor from "@/pages/KPISyncMonitor";
import IntegratedKPICenter from "@/pages/IntegratedKPICenter";
import CapacityProjection from "@/pages/CapacityProjection";
import AISimulations from "@/pages/AISimulations";
import AuditReadyView from "@/pages/AuditReadyView";
import WeeklyReports from "@/pages/WeeklyReports";
import ExecutiveCommandCenter from "@/pages/ExecutiveCommandCenter";
import StrategicPlanDashboard from "@/pages/StrategicPlanDashboard";
import AutomatedImportPage from "@/pages/AutomatedImportPage";
import SystemHealthDashboard from "@/pages/SystemHealthDashboard";
import AlertsDashboard from "@/pages/AlertsDashboard";
import SystemConfiguration from "@/pages/SystemConfiguration";
import DataHistoryPage from "@/pages/DataHistoryPage";
import DataHealthDashboard from "@/pages/DataHealthDashboard";
import BurnoutMonitoring from "@/pages/BurnoutMonitoring";
import CensusPredictions from "@/pages/CensusPredictions";
import CareFlowIntelligence from "@/pages/CareFlowIntelligence";

// High-Value Feature Pages
import PatientAcuity from "@/pages/PatientAcuity";
import PredictiveOutcomes from "@/pages/PredictiveOutcomes";
import NaturalLanguageQuery from "@/pages/NaturalLanguageQuery";
import IntelligentScheduling from "@/pages/IntelligentScheduling";
import GoalTracker from "@/pages/GoalTracker";
import HospiceExcellence from "@/pages/HospiceExcellence";
import DataAdminPanel from "@/pages/DataAdminPanel";
import ROIOnePager from "@/pages/ROIOnePager";
import TeamRecognition from "@/pages/TeamRecognition";
import StaffManagement from "@/pages/StaffManagement";
import LaborUtilization from "@/pages/LaborUtilization";
import PIPIntelligence from "@/pages/PIPIntelligence";
import ClinicalQAAnalyzer from "@/pages/ClinicalQAAnalyzer";
import OperationsCommandCenter from "@/pages/OperationsCommandCenter";
import DocumentIntelligence from "@/pages/DocumentIntelligence";
import GrowthIntelligence from "@/pages/GrowthIntelligence";
// EHRIntegration already imported above

import Layout from "@/components/Layout";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Available color schemes
export const COLOR_SCHEMES = [
  { id: 'default', name: 'Classic Navy', description: 'Professional hospice theme' },
  { id: 'ocean', name: 'Ocean Blue', description: 'Fresh & modern' },
  { id: 'emerald', name: 'Emerald Green', description: 'Calming & natural' },
  { id: 'sunset', name: 'Sunset Warm', description: 'Energetic & bold' },
  { id: 'midnight', name: 'Midnight Purple', description: 'Elegant dark mode' },
  { id: 'coral', name: 'Coral Reef', description: 'Vibrant healthcare' }
];

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('qapi-theme');
    return saved || 'light';
  });

  const [colorScheme, setColorScheme] = useState(() => {
    const saved = localStorage.getItem('qapi-color-scheme');
    return saved || 'default';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    
    // Remove all color scheme classes
    COLOR_SCHEMES.forEach(scheme => {
      root.classList.remove(`theme-${scheme.id}`);
    });
    
    // Apply dark/light mode
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
    
    // Apply color scheme (not needed for default or midnight which is a dark theme)
    if (colorScheme !== 'default') {
      root.classList.add(`theme-${colorScheme}`);
    }
    
    localStorage.setItem('qapi-theme', theme);
    localStorage.setItem('qapi-color-scheme', colorScheme);
  }, [theme, colorScheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, colorScheme, setColorScheme, colorSchemes: COLOR_SCHEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('qapi-token'));
  const [loading, setLoading] = useState(true);
  const [justLoggedIn, setJustLoggedIn] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth check if we just logged in (user is already set)
      if (justLoggedIn) {
        setJustLoggedIn(false);
        setLoading(false);
        return;
      }
      
      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            localStorage.removeItem('qapi-token');
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, justLoggedIn]);

  const login = (newToken, userData) => {
    localStorage.setItem('qapi-token', newToken);
    setJustLoggedIn(true);
    setUser(userData);
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem('qapi-token');
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ConfigProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Dashboard />} />
                <Route path="data-entry" element={<DataEntry />} />
                <Route path="batch-import" element={<BatchImport />} />
                <Route path="pips" element={<PIPs />} />
                <Route path="auto-pip" element={<AutoPIP />} />
                <Route path="auto-pip-analytics" element={<AutoPIPAnalytics />} />
                <Route path="rncm-productivity" element={<RNCMProductivity />} />
                <Route path="capacity-projection" element={<CapacityProjection />} />
                <Route path="pto-calendar" element={<PTOCalendar />} />
                <Route path="hiring-predictions" element={<HiringPredictions />} />
                <Route path="compliance-dashboard" element={<ComplianceDashboard />} />
                <Route path="admissions-discharges" element={<AdmissionsDischarges />} />
                <Route path="digest-settings" element={<DigestSettings />} />
                <Route path="execute-pips" element={<ExecutePIPs />} />
                <Route path="email-settings" element={<EmailSettings />} />
                <Route path="leaderboard" element={<Leaderboard />} />
                <Route path="benchmarking" element={<Benchmarking />} />
                <Route path="customize-dashboard" element={<CustomizeDashboard />} />
                <Route path="training" element={<Training />} />
                <Route path="ehr-integration" element={<EHRIntegration />} />
                <Route path="pip-predictions" element={<PIPPredictions />} />
              <Route path="export-reports" element={<ExportReports />} />
              <Route path="insights" element={<Insights />} />
              <Route path="predictive-analytics" element={<PredictiveAnalytics />} />
              <Route path="trends" element={<Trends />} />
              <Route path="survey-readiness" element={<SurveyReadiness />} />
              <Route path="reports" element={<Reports />} />
              <Route path="custom-metrics" element={<CustomMetrics />} />
              <Route path="compliance" element={<Compliance />} />
              <Route path="security" element={<Security />} />
              <Route path="dashboard-settings" element={<DashboardSettings />} />
              <Route path="collaboration" element={<Collaboration />} />
              <Route path="user-management" element={<UserManagement />} />
              <Route path="burnout-dashboard" element={<BurnoutDashboard />} />
              <Route path="burnout-monitoring" element={<BurnoutMonitoring />} />
              <Route path="ai-agent" element={<AIAgent />} />
              <Route path="ai-intelligence" element={<AIIntelligence />} />
              <Route path="workforce-intelligence" element={<WorkforceIntelligence />} />
              <Route path="clinical-quality" element={<ClinicalQuality />} />
              <Route path="patient-experience" element={<PatientExperience />} />
              <Route path="financial-health" element={<FinancialHealth />} />
              <Route path="chap-readiness" element={<CHAPReadiness />} />
              <Route path="hipaa-compliance" element={<HIPAACompliance />} />
              <Route path="monthly-financials" element={<MonthlyFinancialDashboard />} />
              <Route path="kpi-sync-monitor" element={<KPISyncMonitor />} />
              <Route path="kpi-performance-center" element={<IntegratedKPICenter />} />
              <Route path="ai-simulations" element={<AISimulations />} />
              <Route path="audit-ready" element={<AuditReadyView />} />
              <Route path="weekly-reports" element={<WeeklyReports />} />
              <Route path="command-center" element={<ExecutiveCommandCenter />} />
              <Route path="strategic-plan" element={<StrategicPlanDashboard />} />
              <Route path="automated-import" element={<AutomatedImportPage />} />
              <Route path="system-health" element={<SystemHealthDashboard />} />
              <Route path="alerts" element={<AlertsDashboard />} />
              <Route path="system-config" element={<SystemConfiguration />} />
              <Route path="data-history" element={<DataHistoryPage />} />
              <Route path="data-health" element={<DataHealthDashboard />} />
              <Route path="census-predictions" element={<CensusPredictions />} />
              <Route path="careflow-intelligence" element={<CareFlowIntelligence />} />
              
              {/* High-Value Feature Routes */}
              <Route path="patient-acuity" element={<PatientAcuity />} />
              <Route path="predictive-outcomes" element={<PredictiveOutcomes />} />
              <Route path="ask-data" element={<NaturalLanguageQuery />} />
              <Route path="ehr-integration" element={<EHRIntegration />} />
              <Route path="intelligent-scheduling" element={<IntelligentScheduling />} />
              <Route path="goals" element={<GoalTracker />} />
              <Route path="data-admin" element={<DataAdminPanel />} />
              <Route path="hospice-excellence" element={<HospiceExcellence />} />
              <Route path="roi" element={<ROIOnePager />} />
              <Route path="team-recognition" element={<TeamRecognition />} />
              <Route path="staff-management" element={<StaffManagement />} />
              <Route path="labor-utilization" element={<LaborUtilization />} />
              <Route path="pip-intelligence" element={<PIPIntelligence />} />
              <Route path="clinical-qa" element={<ClinicalQAAnalyzer />} />
              <Route path="operations-center" element={<OperationsCommandCenter />} />
              <Route path="document-intelligence" element={<DocumentIntelligence />} />
              <Route path="growth-intelligence" element={<GrowthIntelligence />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </ConfigProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
